import { useState, MouseEvent, useMemo } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useMatch } from "react-router-dom";
import {
  Box,
  Badge,
  Popover,
  Button,
  Typography as T,
  Stack,
  Typography,
  useTheme,
  Divider,
} from "@mui/material";
import FilterCheckbox from "catalog/components/FilterCheckbox";
import PreferredSwitch from "catalog/components/PreferredSwitch";
import useFilterFacets from "catalog/hooks/useFilterFacets";
import useTagDisplayMappings from "catalog/hooks/useTagDisplayMappings";
import { PREFERRED_ENTITIES_TAG } from "catalog/utils/buildFilterMatrix";
import {
  ArrayParam,
  withDefault,
  useQueryParams,
  useQueryParam,
  NumberParam,
} from "use-query-params";
import { FilterIcon } from "~/assets/icons";
import { Aggregation } from "~/types/interfaces/catalogResponse";

interface FilterForm {
  ttags: string[]; // Table Tags
  ctags: string[]; // Column Tags
}

interface TagGroup {
  key: string;
  label: string;
  count: number;
}

const COLLAPSE_LIMIT = 10;

const ExpansionButton = (props: { open: boolean; handleClick: () => void }) => (
  <Button size="small" sx={{ display: "block" }} onClick={props.handleClick}>
    Show {props.open ? "Less" : "More"}
  </Button>
);

interface TagGroupProps {
  title: string;
  queryKey: string;
  group: TagGroup[];
}

const TagGroup = ({ title, group, queryKey }: TagGroupProps) => {
  if (!group || !group.length) {
    return null;
  }
  return (
    <Box mb={1.25} display="flex" flexDirection="column">
      <T variant="label" sx={{ fontWeight: 600, fontSize: 13, opacity: 1, mb: 0.5 }}>
        {title}
      </T>
      {group
        .sort((a, b) => a.label.localeCompare(b.label))
        .map(({ key, label, count }) => (
          <FilterCheckbox
            key={key}
            name={queryKey}
            facetKey={key}
            label={
              <Typography
                component="span"
                fontSize={14}
                color={"inherit"}
                lineHeight={1}
                whiteSpace="nowrap"
              >
                {label}
                {count !== undefined && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      ml: 0.5,
                      color: "grey.600",
                    }}
                  >
                    ({count.toLocaleString()})
                  </Box>
                )}
              </Typography>
            }
          />
        ))}
    </Box>
  );
};

interface FilterFormSection {
  title: string;
  queryKey: string;
  tagOptions: Aggregation["buckets"];
}

const FilterFormSection = ({ tagOptions, queryKey }: FilterFormSection) => {
  const [showMore, setShowMore] = useState<Record<string, boolean>>({ ttags: false, ctags: false });
  const tagDisplayMappings = useTagDisplayMappings();

  const tagGroups = useMemo(() => {
    const groupMap: Record<string, TagGroup[]> = {};

    tagOptions.forEach(({ key, doc_count }) => {
      const parts = key.split(".");
      const displayMappings = tagDisplayMappings[key];
      const classification = displayMappings?.classification || parts[0];
      const tagName = displayMappings?.tagName || parts[1];

      if (parts[0] === "ux") {
        // Skip displaying UX tags
        return;
      }

      if (parts.length > 1) {
        if (!(classification in groupMap)) {
          groupMap[classification] = [];
        }
        groupMap[classification].push({ key, label: tagName, count: doc_count });
      } else {
        /**
         * If key doesn't have a classification, organize in a generic "Tags" group.
         */
        if (!("Tags" in groupMap)) {
          groupMap["Tags"] = [];
        }
        groupMap["Tags"].push({ key, label: tagName, count: doc_count });
      }
    });
    return groupMap;
  }, [tagOptions, tagDisplayMappings]);

  const tagGroupsCount = (tagGroups && Object.keys(tagGroups).length) || 0;

  return (
    <Box>
      <Box display="grid" columnGap={1} gridTemplateColumns="repeat(1, 1fr)" minWidth={225}>
        {Object.keys(tagGroups)
          .sort()
          .map((key) => {
            return <TagGroup key={key} title={key} group={tagGroups[key]} queryKey={queryKey} />;
          })
          .slice(0, showMore[queryKey] ? tagGroupsCount : COLLAPSE_LIMIT)}
      </Box>
      {tagGroupsCount > COLLAPSE_LIMIT && (
        <ExpansionButton
          open={showMore[queryKey]}
          handleClick={() => setShowMore({ ...showMore, [queryKey]: !showMore[queryKey] })}
        />
      )}
    </Box>
  );
};

const FilterForm = (props: { sections: FilterFormSection[]; handleClose: () => void }) => {
  const [, setPage] = useQueryParam("page", NumberParam);
  const [filters, setFilters] = useQueryParams({
    ttags: withDefault(ArrayParam, []),
    ctags: withDefault(ArrayParam, []),
  });
  const form = useForm<FilterForm>({
    defaultValues: filters as FilterForm,
  });

  const filtersWatch = useWatch({ control: form.control, name: ["ttags", "ctags"] });
  const hasFiltersSelected = Object.values(filtersWatch).some((val) =>
    Array.isArray(val) ? val.length : val
  );
  const hasFiltersApplied = Object.values(filters).some((val) =>
    Array.isArray(val) ? val.length : val
  );
  const hasFilterOptions = props.sections.length !== 0;
  const hasPreferredFilter = props.sections.some(({ tagOptions }) =>
    tagOptions.some((tag) => tag.key === PREFERRED_ENTITIES_TAG)
  );
  const hasTagOptions = props.sections.some(({ tagOptions }) =>
    tagOptions.some((tag) => tag.key !== PREFERRED_ENTITIES_TAG)
  );
  const multipleSections = props.sections.length > 1;

  const resetPage = () => setPage(undefined, "replaceIn");

  const onSubmit = (filters: FilterForm) => {
    resetPage();
    setFilters(filters);
    props.handleClose();
  };

  const handleClear = () => {
    resetPage();
    setFilters({ ttags: undefined, ctags: undefined }, "replaceIn");
    props.handleClose();
  };

  return (
    <Box component="form" sx={{ userSelect: "none" }} onSubmit={form.handleSubmit(onSubmit)}>
      <FormProvider {...form}>
        {!hasFilterOptions ? (
          <Box display="flex" flexDirection="column" alignItems="center" p={2}>
            <T variant="body2" color="grey.600">
              No filters available
            </T>
            {hasFiltersApplied && (
              <Button sx={{ mt: 1 }} variant="outlined" color="inherit" onClick={handleClear}>
                Clear
              </Button>
            )}
          </Box>
        ) : (
          <Box>
            <Stack>
              {hasPreferredFilter && (
                <>
                  <Box
                    display="flex"
                    p={2}
                    gap={2}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <T variant="body2">
                      Preferred {multipleSections ? "pins" : props.sections[0].title.toLowerCase()}{" "}
                      only
                    </T>
                    <PreferredSwitch />
                  </Box>
                  {hasTagOptions && <Divider />}
                </>
              )}
              <Box sx={{ maxHeight: 400, py: 1, px: 2, overflowY: "auto" }}>
                {props.sections.map((section, idx) => (
                  <Stack key={section.title}>
                    {multipleSections && (
                      <>
                        {idx !== 0 && <Divider sx={{ mb: 2 }} />}
                        <T variant="label" mb={1}>
                          {section.title}
                        </T>
                      </>
                    )}
                    <FilterFormSection {...section} />
                  </Stack>
                ))}
              </Box>
            </Stack>
            <Divider />
            <Box
              bgcolor="common.white"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              px={2}
              py={1.5}
            >
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                disabled={!hasFiltersSelected}
                onClick={handleClear}
              >
                Clear
              </Button>
              <Button type="submit" variant="contained" size="small">
                Apply
              </Button>
            </Box>
          </Box>
        )}
      </FormProvider>
    </Box>
  );
};

const FilterButton = () => {
  const { palette } = useTheme();
  const match = useMatch("/data-browser/:tab/*");
  const tab = match?.params.tab ?? "";
  const { data: tableData, isLoading: loadingTableData } = useFilterFacets(
    "table",
    ["tables", "pins"].includes(tab)
  );
  const { data: columnData, isLoading: loadingColumnFacets } = useFilterFacets(
    "column",
    ["columns", "pins"].includes(tab)
  );
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [filters] = useQueryParams({
    ttags: withDefault(ArrayParam, []),
    ctags: withDefault(ArrayParam, []),
  });

  const sections = useMemo(() => {
    const result = [];
    const columnTags = columnData?.aggregations?.["tags.tagFQN.keyword"]?.buckets;
    const tableTags = tableData?.aggregations?.["tags.tagFQN.keyword"]?.buckets;
    if (columnTags?.length) {
      result.push({
        title: "Columns",
        queryKey: "ctags",
        tagOptions: columnTags.filter((tag) => tag),
      });
    }
    if (tableTags?.length) {
      result.push({
        title: "Tables",
        queryKey: "ttags",
        tagOptions: tableTags,
      });
    }

    return result;
  }, [tableData, columnData, tab]);

  const numberFiltersApplied = useMemo(() => {
    if (tab === "columns") {
      return filters.ctags.length;
    }
    if (tab === "tables") {
      return filters.ttags.length;
    }
    return filters.ctags.length + filters.ttags.length;
  }, [tab, filters]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Badge
        badgeContent={numberFiltersApplied > 0 ? numberFiltersApplied : null}
        color="primary"
        sx={{
          "& > span": {
            fontSize: 10,
            top: 2,
            height: 16,
            minWidth: 16,
            fontWeight: 500,
            outline: `3px solid ${palette.common.pageBackground}`,
            userSelect: "none",
          },
        }}
      >
        <Button
          color="inherit"
          disabled={loadingTableData || loadingColumnFacets}
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
          }}
          sx={{ display: "flex", gap: 1 }}
        >
          <FilterIcon />
          Filter
        </Button>
      </Badge>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionProps={{ timeout: 0 }}
        PaperProps={{
          elevation: 2,
          sx: { maxWidth: 420 },
        }}
        sx={{
          mt: 1,
        }}
      >
        <FilterForm sections={sections} handleClose={handleClose} />
      </Popover>
    </>
  );
};

export default FilterButton;
